/**
 * Key format for everything the SDK and its host apps put in browser storage.
 *
 * All keys are `bayun:<version>:<appId>:<scope>:...` so that:
 *  - two Bayun apps served from one origin cannot collide,
 *  - every key belonging to one session can be found by prefix and deleted, and
 *  - a future format change can be migrated by version segment.
 *
 * The separator is ":" and it may not appear in an appId, sessionId, or flowId. That is what makes
 * the segments unambiguous — the previous scheme concatenated ids with no separator, so
 * ("ab", "c12") and ("abc", "12") produced the same key.
 */

const NAMESPACE = "bayun";
const VERSION = "v1";

/** Scope markers. Kept to one character because these are repeated in every key. */
const SCOPE_SESSION = "s";
const SCOPE_DEVICE = "d";
const SCOPE_FLOW = "f";
const SCOPE_TAB = "t";

/**
 * The registry of signed-in sessions. Deliberately outside the appId segment: it is looked up
 * before any session (and therefore any appId) is known.
 */
export const REGISTRY_KEY = `${NAMESPACE}:${VERSION}:sessions`;

/** Identifier of the state blob within a session's scope. */
export const STATE_KEY = "state";

let activeAppId = "";
let appIdPinned = false;

/**
 * Records the appId every key is built under. Called from the BayunCore constructor.
 *
 * The first call wins; a later different one is refused with a warning. Moving the namespace after
 * data exists does not migrate it — the old keys just become unreachable, including to the logout
 * prefix scan. The Dev Portal hit this: one of its 27 init calls passes an appId and the rest pass
 * none, so a session appeared to vanish mid-flow.
 *
 * An empty appId pins too. It means "this app has no identity of its own", not "not set yet" — its
 * data is already under the "_" segment, and accepting a real appId later would strand it.
 */
export const setActiveAppId = (appId) => {
  const next = appId ? String(appId) : "";
  if (appIdPinned) {
    if (next && next !== activeAppId) {
      console.warn(
        `Bayun: storage is already namespaced under appId "${activeAppId || "_"}"; ` +
          `ignoring "${next}". Initialise the SDK once per page with one appId.`,
      );
    }
    return;
  }
  appIdPinned = true;
  activeAppId = next;
};

/** Test seam. Not exported through BayunCore — production code must never reset the namespace. */
export const resetActiveAppIdForTests = () => {
  activeAppId = "";
  appIdPinned = false;
};

export const getActiveAppId = () => activeAppId;

/**
 * Apps built before the SDK knew its appId, and a handful of test entry points, reach storage
 * without configuring one. Those keys land under "_" rather than silently sharing a namespace with
 * a real app.
 */
const appSegment = () => activeAppId || "_";

const assertSegment = (value, name) => {
  const segment = value == null ? "" : String(value);
  if (!segment) {
    throw new Error(`${name} is required to build a storage key.`);
  }
  if (segment.includes(":")) {
    throw new Error(`${name} may not contain ":" — it is the storage key separator.`);
  }
  return segment;
};

/**
 * Prefix owned by one session. Every key under it is removed together on logout.
 */
export const sessionPrefix = (sessionId) =>
  `${NAMESPACE}:${VERSION}:${appSegment()}:${SCOPE_SESSION}:${assertSegment(
    sessionId,
    "sessionId",
  )}:`;

export const sessionKey = (sessionId, identifier) =>
  `${sessionPrefix(sessionId)}${assertSegment(identifier, "identifier")}`;

/**
 * Device scope is for values with no account identity — theme, locale, "remember this device".
 * Anything that describes an account belongs in session scope instead.
 */
export const deviceKey = (identifier) =>
  `${NAMESPACE}:${VERSION}:${appSegment()}:${SCOPE_DEVICE}:${assertSegment(
    identifier,
    "identifier",
  )}`;

/**
 * Flow scope covers pre-authentication activity — signup being the case that exists today — where
 * there is no sessionId to scope by yet. Flow keys live in sessionStorage so two tabs can run two
 * signups at once.
 */
export const flowPrefix = (flowId) =>
  `${NAMESPACE}:${VERSION}:${appSegment()}:${SCOPE_FLOW}:${assertSegment(flowId, "flowId")}:`;

export const flowKey = (flowId, identifier) =>
  `${flowPrefix(flowId)}${assertSegment(identifier, "identifier")}`;

/**
 * Tab scope is for values that describe this browser tab rather than any account — which account
 * the tab is currently showing, above all. Tab keys live in sessionStorage, which is what makes
 * them per-tab; the key only keeps two apps on one origin from colliding.
 */
export const tabKey = (identifier) =>
  `${NAMESPACE}:${VERSION}:${appSegment()}:${SCOPE_TAB}:${assertSegment(
    identifier,
    "identifier",
  )}`;
